import React from "react";
import { BUTTON_VARIANTS } from "../../atoms/button/button.shared";
import { Button, type ButtonVariant } from "../../atoms/button/button";
import {
  TEXT_BODY_ELEMENTS,
  TEXT_BODY_VARIANTS,
  TEXT_HEADING_VARIANTS,
  TEXT_LEVELS,
  Text,
  hasMarkup,
  plainText,
  safeElement,
  safeVariant
} from "../../atoms/text/text";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeHref } from "../../services/safe-href";
import { MediaControls } from "./media-controls.client";
import styles from "./marquee-media.module.css";
import type { MarqueeMediaProps } from "./marquee-media.types";
import { withSafeRender } from "../../atoms/safe-render";

/** The widths the two still crops are drawn for; see grid.css. */
const WIDE_MEDIA = "(min-width: 1280px)";
const TABLET_MEDIA = "(min-width: 735px)";

/* Button's own set, not a copy of it: one definition, so a treatment published
   there can never be a value an author sets here and never sees. Only two are
   drawn by any frame in this section, an open entry on
   design-conflicts/marquee-media.mdx. */
export const CTA_VARIANTS: Record<ButtonVariant, true> = BUTTON_VARIANTS;

/** The value if the set lists it, the fallback if not, so a CMS string can never
 *  reach a same-named attribute selector. `hasOwnProperty` rather than a bracket
 *  lookup, which would admit `constructor` and friends. */
function published<T extends string>(
  set: Record<T, true>,
  value: string,
  fallback: T
): T {
  return Object.prototype.hasOwnProperty.call(set, value) ? (value as T) : fallback;
}

/** A text field's content, or "" for anything that is not text with ink in it. */
function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * A switch field's state — true only for the boolean `true`.
 *
 * NOT A TRUTHINESS TEST. Storyblok can publish an unset switch as the STRING
 * "false", which is truthy, so any field whose absent state must mean ON has to
 * be spelled as the suppression. That is why the field is `hide_mute_control`:
 * `show_mute_control` would invert the published default the moment a real CMS
 * row arrived. Faq and Footer carry the same guard.
 */
function flag(value: unknown): boolean {
  return value === true;
}

/** A place a button is drawn, named for the treatment it falls back to. The
 *  frames draw these two and no more, so the cap is the shape rather than a
 *  length the block has to police. */
type CtaSlot = "primary" | "secondary";

/** One slot's three fields as published, read as `unknown` because a CMS feed
 *  reaches this past the compiler. */
interface CtaFields {
  ariaLabel: unknown;
  href: unknown;
  label: unknown;
  slot: CtaSlot;
  variant: unknown;
}

/** One slot's button, resolved and ready to draw. */
interface DrawnCta {
  ariaLabel?: string;
  href: string;
  label: string;
  slot: CtaSlot;
  variant: ButtonVariant;
}

/** The buttons the block may draw, a slot short of either half left out so the
 *  other slot is unaffected. ⚠ A destination is CMS content on a real anchor:
 *  `services/safe-href` also refuses `tel:` and `mailto:`.
 *  ⚠ THE VARIANT FALLBACK IS THE SLOT'S OWN NAME. What the frames publish is
 *  the pair — primary in the lead, secondary in the trail — so the trailing
 *  slot stays secondary with the leading one empty rather than being promoted
 *  for standing alone. ⚠ THAT LONE CASE FOLLOWS FROM THE RULE AND NOT FROM A
 *  DRAWING: no frame in the section draws a one-button row. */
function usableCtas(slots: CtaFields[], report = false): DrawnCta[] {
  const drawn: DrawnCta[] = [];

  for (const { ariaLabel, href, label, slot, variant } of slots) {
    const wording = text(label);
    const destination = text(href);

    /* Neither half filled is a slot the author left alone rather than a
       mistake, so it is passed over in silence — ⚠ UNLESS THE TREATMENT IS
       SET, which is the one thing in an empty slot that proves the author
       opened it. Reporting the empty pair itself would fire on every block
       drawing one button, and on every block drawing none. */
    if (wording === "" && destination === "") {
      if (report && text(variant) !== "") {
        console.error(
          `[MarqueeMedia] ${slot}_cta_variant is set on a button with neither wording nor a destination, so nothing is drawn. Fill in ${slot}_cta_label and ${slot}_cta_url, or clear the treatment.`
        );
      }

      continue;
    }

    if (wording === "") {
      if (report) {
        console.error(
          `[MarqueeMedia] the ${slot} button has a destination but no wording, so it is dropped and shows nowhere. Fill in ${slot}_cta_label, or clear ${slot}_cta_url.`
        );
      }

      continue;
    }

    if (!isSafeHref(destination)) {
      if (report) {
        console.error(
          `[MarqueeMedia] the ${slot} button has no usable destination, so it is dropped. Give ${slot}_cta_url an https, http, tel or mailto address, or one written against directv.com such as "/plans" or "#offers" — an empty one, and a tel: or mailto: one, are all refused.`
        );
      }

      continue;
    }

    const treatment = text(variant);
    const drawnAs = published(CTA_VARIANTS, treatment, slot);

    /* ⚠ THE MIRROR OF THE EMPTY SLOT ABOVE, AND THE HALF THAT COSTS THE AUTHOR
       SOMETHING: the button is drawn either way, in a treatment nobody picked,
       so nothing on screen shows the value went nowhere. */
    if (report && treatment !== "" && drawnAs !== treatment) {
      console.error(
        `[MarqueeMedia] ${slot}_cta_variant is set to "${treatment}", which is not a treatment this block draws, so the ${slot} button is drawn in the ${drawnAs} treatment. Set ${slot}_cta_variant to one of ${Object.keys(CTA_VARIANTS).join(", ")}; or clear it.`
      );
    }

    drawn.push({
      ariaLabel: ctaAriaLabel(ariaLabel),
      href: destination,
      label: wording,
      slot,
      variant: drawnAs
    });
  }

  return drawn;
}

/** The three fields a picture can arrive in, in the order the block reads
 *  them. Named so a diagnostic quotes the API field an author would have to
 *  change rather than the one crop that is drawn at every width. */
const PICTURE_FIELDS = ["image_url", "image_tablet_url", "image_mobile_url"] as const;

/** Every field the diagnostics read, trimmed as published. ⚠ THE PICTURE
 *  FIELDS ARE READ AS A SET as well as one at a time: any of the three filled
 *  beside a film is a picture that shows nowhere, so those three keys — and
 *  `heading_level` — carry the API names the messages quote. */
interface PublishedBlock {
  alt: string;
  heading: string;
  heading_level: string;
  image_mobile_url: string;
  image_tablet_url: string;
  image_url: string;
  slots: CtaFields[];
  video_url: string;
}

/** The picture fields with ink in them, by the name the CMS gives each one. */
function filledPictures(block: PublishedBlock): string[] {
  return PICTURE_FIELDS.filter(field => block[field] !== "");
}

/** The block drawing nothing at all, said on EVERY build.
 *  ⚠ THE ONE MESSAGE OUTSIDE THE DEVELOPMENT GATE, because it is the one
 *  mistake with no evidence on the page: an author who never opens a
 *  development build otherwise has nothing at all to tell them their block
 *  vanished. ⚠ AND IT QUOTES NO CMS CONTENT, field names only — this one
 *  reaches a production console, where an author string must not. */
function reportNothingDrawn(block: PublishedBlock): void {
  if (block.video_url !== "" || block.image_url !== "") {
    return;
  }

  /* ⚠ A CROP FILLED HERE IS A PICTURE THE AUTHOR CAN SEE THEY TYPED IN, so
     the field it sits in is the one thing they will be looking at. */
  let crops = "";

  if (filledPictures(block).length > 0) {
    crops = " A narrow crop draws nothing on its own: image_tablet_url and " +
      "image_mobile_url stand in for image_url at one width each, and " +
      "image_url is empty.";
  }

  console.error(
    "[MarqueeMedia] has neither a video nor a picture, so the whole block " +
      "renders nothing at all and everything written on it is lost. Fill in " +
      `video_url, or image_url.${crops}`
  );
}

/** What is wrong with the block, said once per render on a development build.
 *  ⚠ FOUR MESSAGES ARE DELIBERATELY NOT HERE. Two report a no-op the design
 *  asks for rather than a mistake — `hide_mute_control` and `poster_url` on the
 *  still path. The third, an empty `media_alt` on the film, is left out because
 *  nothing here can tell a film that is pure background motion from one the
 *  description was forgotten on, and only the first is exempt. The fourth is
 *  the block drawing nothing, which is said above on every build instead. */
function reportProblems(block: PublishedBlock): void {
  const {
    alt,
    heading,
    heading_level: headingLevel,
    image_url: imageUrl,
    slots,
    video_url: videoUrl
  } = block;

  const pictures = filledPictures(block);

  /* ⚠ EVERY PICTURE FIELD, NOT image_url ALONE. The two crops are read on the
     still path only, so one filled beside a film is discarded on exactly the
     same terms — and a message naming neither leaves the author looking at a
     field nothing reports. */
  if (videoUrl !== "" && pictures.length > 0) {
    console.error(
      "[MarqueeMedia] carries a video AND a picture. The video is drawn and " +
        `nothing filled into ${pictures.join(", ")} shows anywhere — clear ` +
        "the video or the picture, and use poster_url for the frame held " +
        "while the video loads."
    );
  }

  if (heading === "") {
    console.error(
      "[MarqueeMedia] has no headline, so the block opens on body copy with " +
        "nothing over it and leaves a level missing from the page's outline. " +
        "Fill it in."
    );
  }

  /* ⚠ A HEADLINE FIELD IS NOT GUARANTEED TO RENDER AS A HEADING: `Text` draws a
     div wherever the content carries block markup, overriding the level asked
     for, and the block then leaves the page's outline. */
  if (hasMarkup(heading)) {
    console.error(
      "[MarqueeMedia] the headline carries markup of some kind, and it has to " +
        "be plain text. Inline markup is safe, but if any of it is block-level " +
        "the heading is already gone: it draws as an ordinary container rather " +
        "than a heading, which takes the block out of the page's outline. Move " +
        "the markup into the body copy."
    );
  }

  /* An unrecognised level is neither drawn as asked nor dropped: the headline
     still opens, at the fallback, so nothing on the page shows the author that
     the value they typed went nowhere. */
  if (
    headingLevel !== "" &&
    published(TEXT_LEVELS, headingLevel, "h2") !== headingLevel
  ) {
    console.error(
      `[MarqueeMedia] heading_level is set to "${headingLevel}", which is not a level this block opens the headline at, so it opens at h2. Set heading_level to one of ${Object.keys(TEXT_LEVELS).join(", ")}; or clear it.`
    );
  }

  /* ⚠ THE STILL PATH ALONE, and not because a film needs no description. A
     picture is the block's subject, so an empty one is always wrong there. On
     the film it may be right or wrong depending on what the footage shows, and
     that is a judgement about the asset rather than about the path — see the
     note on this function. */
  if (videoUrl === "" && imageUrl !== "" && alt === "") {
    console.error(
      "[MarqueeMedia] the picture has no description, so it is marked as " +
        "decoration and announces nothing at all. Describe what is in it — the " +
        "headline is read out beside it either way, so do not type that again."
    );
  }

  usableCtas(slots, true);
}

/** Marquee Media — a page's opening block: a film playing by itself, or a still
 *  picture, under a gradient carrying a headline, body copy and up to two
 *  buttons. The global nav sits over it on the Z axis and is a component of its
 *  own; the desktop film's 48px top inset is its only trace here. */
function MarqueeMediaImpl({
  video_url: videoUrl,
  poster_url: posterUrl,
  image_url: imageUrl,
  image_tablet_url: imageTabletUrl,
  image_mobile_url: imageMobileUrl,
  media_alt: mediaAlt,
  hide_mute_control: hideMuteControl,
  heading_content: headline,
  heading_level: headingLevel,
  heading_variant: headlineVariant,
  body_content: body,
  body_variant: bodyVariant,
  body_element: bodyElement,
  primary_cta_label: primaryCtaLabel,
  primary_cta_url: primaryCtaUrl,
  primary_cta_variant: primaryCtaVariant,
  primary_cta_aria_label: primaryCtaAriaLabel,
  secondary_cta_label: secondaryCtaLabel,
  secondary_cta_url: secondaryCtaUrl,
  secondary_cta_variant: secondaryCtaVariant,
  secondary_cta_aria_label: secondaryCtaAriaLabel
}: MarqueeMediaProps) {
  const video = text(videoUrl);
  const image = text(imageUrl);
  const tablet = text(imageTabletUrl);
  const mobile = text(imageMobileUrl);
  const poster = text(posterUrl);
  const alt = text(mediaAlt);
  const heading = text(headline);
  const copy = text(body);
  const levelAsked = text(headingLevel);
  const level = safeElement(levelAsked, "h2", TEXT_LEVELS);
  const headlineStyle = safeVariant(text(headlineVariant), "headline-1", TEXT_HEADING_VARIANTS);
  const bodyStyle = safeVariant(text(bodyVariant), "body-2", TEXT_BODY_VARIANTS);
  const bodyAs = safeElement(text(bodyElement), "p", TEXT_BODY_ELEMENTS);

  /* THE BLOCK'S OWN NAME, AND THE GATE ON ITS LANDMARK IN ONE VALUE. ⚠ TAKEN
     OFF THE HEADLINE'S WORDING RATHER THAN aria-labelledby: `useId` cannot run
     in a Server Component, and an id fixed by hand collides the day a page
     draws two of these. ⚠ AND NO ROLE AT ALL WITHOUT IT — an unnamed region is
     a landmark a reader meets in the list and cannot tell from any other. */
  const name = plainText(heading) || undefined;

  /* In the order the frames draw them, which is also the order they are read
     out: the slot decides the treatment, so neither one moves. */
  const slots: CtaFields[] = [
    {
      ariaLabel: primaryCtaAriaLabel,
      href: primaryCtaUrl,
      label: primaryCtaLabel,
      slot: "primary",
      variant: primaryCtaVariant
    },
    {
      ariaLabel: secondaryCtaAriaLabel,
      href: secondaryCtaUrl,
      label: secondaryCtaLabel,
      slot: "secondary",
      variant: secondaryCtaVariant
    }
  ];
  const buttons = usableCtas(slots);

  const fields: PublishedBlock = {
    alt,
    heading,
    heading_level: levelAsked,
    image_mobile_url: mobile,
    image_tablet_url: tablet,
    image_url: image,
    slots,
    video_url: video
  };

  /* ⚠ UNGATED, ALONE AMONG THE DIAGNOSTICS: the block renders nothing on this
     path, so there is nothing on the page for an author to read the mistake
     off — and a CMS author need never open a development build. */
  reportNothingDrawn(fields);

  /* ⚠ SAID BEFORE THE EMPTY BLOCK BAILS, or the one authoring mistake that
     costs every field is the one nothing reports. */
  if (process.env.NODE_ENV !== "production") {
    reportProblems(fields);
  }

  if (video === "" && image === "") {
    return null;
  }

  return (
    /* ⚠ A div AND NOT A section. Both canvases already wrap every blok in a
       <section> carrying the page anchor and the content-visibility rule, so a
       second one nests and duplicates the id an anchor points at — the landmark
       is this role rather than an element of its own. */
    <div
      aria-label={name}
      className={styles.root}
      data-mm-root="true"
      role={name === undefined ? undefined : "region"}
    >
      <div className={video === "" ? `${styles.media} ${styles.still}` : styles.media}>
        {video === "" ? (
          <picture>
            {/* Two crops over the base one rather than one reflowed picture:
                the frames draw 1440/813, 768/555 and 402/395, which no
                object-fit reaches from a single file. The conditions are
                literals because a media query cannot read a custom property. */}
            <source media={WIDE_MEDIA} srcSet={image} />
            <source media={TABLET_MEDIA} srcSet={tablet || image} />
            {/* ⚠ THE DESCRIPTION IS EMITTED EVEN WHEN BLANK rather than the
                attribute dropped: an img with no alt announces its filename.
                ⚠ NOT DRAGGABLE, or a press on it starts the browser's own
                image drag.
                ⚠ camelCase PROP, LOWERCASE ATTRIBUTE: @storybook/nextjs and the
                app render through Next's compiled React 19, not the declared
                react-dom 18.3.1 — it faults the lowercase spelling, and the
                parser lowercases the DOM attribute from either. */}
            <img
              alt={alt}
              className={styles.mediaEl}
              draggable={false}
              fetchPriority="high"
              src={mobile || image}
            />
          </picture>
        ) : (
          /* ⚠ NO role AND NO aria-hidden: the film is the block's subject, and
             its accessible name is `media_alt` where the author gave one — where
             the field is empty NO attribute is emitted, never an empty one.
             ⚠ AND NO CAPTIONS TRACK: no frame publishes a captions file, and a
             fabricated one advertises captions that do not exist. What keeps
             that from biting is a content rule this block cannot enforce.
             ⚠ preload is `auto` AND THE HINT IS NOT WHAT DECIDES THE FETCH:
             `autoPlay` pulls the film whatever this says — `auto`, `metadata`
             and `none` measured byte-identical in Chromium — so it states the
             intent and buys nothing. The bytes are the placement's. */
          <video
            aria-label={alt || undefined}
            autoPlay
            className={styles.film}
            data-mm-media="true"
            loop
            muted
            playsInline
            poster={poster || undefined}
            preload="auto"
            src={video}
          />
        )}
      </div>

      <div className={video === "" ? styles.scrim : `${styles.scrim} ${styles.underFilm}`}>
        {/* ⚠ THE CONTROLS PRECEDE THE COPY IN DOM ORDER, because they sit over
            it visually at every width (SC 1.3.2, SC 2.4.3) — the one place this
            block parts from its two precedents. The row is a SIBLING of the
            copy rather than a wrapper around it, so the DOM is identical on
            both media paths bar this row. ⚠ AND IT IS DRAWN ON THE FILM PATH
            ALONE, which is the annotation "if it's an image, video controls are
            always suppressed" made structural rather than a prop a CMS mis-fill
            could flip: the still path ships no PLAYBACK JavaScript at all.
            ⚠ NO PLAYBACK JavaScript RATHER THAN NONE: the CTA row's Button
            carries "use client" of its own and renders on both paths, so a
            block with buttons hydrates either way. */}
        {video !== "" && <MediaControls showMute={!flag(hideMuteControl)} />}

        <div className={styles.copy}>
          {heading !== "" && (
            /* ⚠ THE SIZE AND LEADING ARE THE FRAMES', NOT THE RAMP'S, pinned in
               the stylesheet for the default variant only. */
            <Text
              as={level}
              className={[styles.headline, headlineStyle === "headline-1" ? styles.headlineType : null].filter(Boolean).join(" ")}
              variant={headlineStyle}
              weight="medium"
            >
              {heading}
            </Text>
          )}

          {(copy !== "" || buttons.length > 0) && (
            <div className={styles.copyBlock}>
              {copy !== "" && (
                /* Rich text, so the copy may carry the inline link the frames
                   draw. ⚠ NOTHING IS FILTERED on the way through — `Text`
                   records that in its own header, and this block adds no
                   sanitiser and no dangerouslySetInnerHTML, because a half-gate
                   that looks like a gate is worse than none. */
                <Text
                  as={bodyAs}
                  className={[styles.body, bodyStyle === "body-2" ? styles.bodyType : null].filter(Boolean).join(" ")}
                  variant={bodyStyle}
                >
                  {copy}
                </Text>
              )}

              {buttons.length > 0 && (
                <div className={styles.ctas}>
                  {buttons.map(cta => (
                    /* ⚠ ATTRIBUTES, NOT A PUSH: the container reports only an
                       element carrying these two — a bare anchor reports nothing.
                       `linkAttributes` writes them; a push of our own would
                       count it twice. */
                    <Button
                      ariaLabel={cta.ariaLabel}
                      className={styles.cta}
                      href={cta.href}
                      key={cta.slot}
                      linkAttributes
                      linkPosition={LINK_POSITION_BODY}
                      size="small"
                      variant={cta.variant}
                    >
                      {cta.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
export const MarqueeMedia = withSafeRender(MarqueeMediaImpl, "MarqueeMedia");

export default MarqueeMedia;
